// src/components/outputs/pieces/cleanCopy.js — a hand selection inside an output box copies clean.
//
// Chrome and Edge serialise a selection with the computed styles inlined, so
// text selected by hand in a value box of PieceRow / ReadyToPublish lands in
// Word and Gmail with the box's tinted background, its border, the theme ink
// and the accent on links. This module rebuilds the clipboard payload from
// the LIVE DOM of the selection instead: allowlisted tags only, no attribute
// but an http(s) href, the text escaped by hand. It never assigns innerHTML
// and never parses a string of markup (fe/CLAUDE.md § Outputs: no innerHTML
// and no sanitiser in outputs) — it walks nodes and concatenates strings.
//
//   serializeSelection(range, box) → { html, text } | null
//     null (the browser copies as today) when the range is collapsed, when its
//     start is outside `box`, when it starts inside the HTML source piece
//     ([data-testid="piece-source"]: that text IS markup and is copied
//     verbatim), when it really continues past the box (a drag into the next
//     section: the user selected more than one box, the browser serves the
//     whole selection), or when nothing but whitespace is selected. An end
//     outside the box with NO text after the box (a triple-click ends at
//     offset 0 of the next block) is clamped to the end of the box.
//     The cloned fragment is re-wrapped in its allowlisted ancestors up to the
//     box: inline ancestors always (a selection inside one <strong> gives
//     "<strong>…</strong>"), block ancestors only when the fragment itself
//     holds a non-empty block (two <li> of a <ul> give
//     "<ul><li>…</li><li>…</li></ul>"; a few words inside one paragraph stay
//     inline, as the browser does, so they paste mid-sentence). An empty
//     block (the leading or trailing <p></p> / <li></li> a boundary offset
//     clones) is dropped.
//     `text` is built in the same walk (Range.toString() joins text nodes with
//     no breaks at all): a blank line between blocks, one line per list item
//     with "• " (ul) or "N. " (ol), "\n" for a <br> — the shape of the .txt
//     serialiser (publish/serialize/plain.js). N is the item's real position
//     when the selection lies within one list; a list cloned whole inside a
//     wider selection numbers from 1. The HTML carries no `start` attribute
//     (href is the only attribute kept), so Word numbers a partial <ol> from 1.
//   Tags kept:  p h1 h2 h3 h4 ul ol li strong em a sup br (h1: the blog's title)
//               a keeps only an http(s) href (isHttpHref); a non-http anchor is
//               unwrapped. Every other attribute (style, class, data-*, target,
//               rel) is dropped.
//   div / section: become <p> when they hold inline content only (the slide
//               label, the chips row), are unwrapped when they hold blocks
//               (BlocksView's wrapper, a qa block, a slide card).
//   Anything else (span, details, summary, pre…): unwrapped, children kept.
//   Text:       & < > escaped; & " < > in href.
//
//   onCopyWithinBox(boxRef) → React onCopy handler for the box element: with
//     exactly one selection range and a non-null serialisation it sets
//     text/html + text/plain on event.clipboardData and preventDefault()s;
//     without clipboardData (an old engine) it does nothing. The copy event
//     reaches the box that holds the START of the selection, so a selection
//     dragged backwards from another box never gets here and copies as today.
//
// Pure DOM, no React import: the two boxes wire it, the tests drive it through
// document.createRange() + getSelection().addRange().

import { isHttpHref } from '../../../publish/normalize.js';

const SOURCE_SELECTOR = '[data-testid="piece-source"]';

const BLOCK_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li']);
const INLINE_TAGS = new Set(['strong', 'em', 'a', 'sup', 'br']);
const CONTAINER_TAGS = new Set(['div', 'section']);
const BLOCK_LIKE = new Set([...BLOCK_TAGS, ...CONTAINER_TAGS, 'details', 'pre', 'h5', 'h6', 'blockquote', 'table']);

const ELEMENT = 1;
const TEXT = 3;
const FRAGMENT = 11;

const EMPTY = { html: '', text: '', block: false };

const escText = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s) => escText(s).replace(/"/g, '&quot;');

const tagOf = (node) => (node && node.nodeType === ELEMENT ? node.tagName.toLowerCase() : '');

/** The open/close pair of an allowlisted element, or null when it must be unwrapped. */
function wrapperOf(node) {
  const tag = tagOf(node);
  if (tag === 'a') {
    const href = node.getAttribute('href');
    return isHttpHref(href) ? [`<a href="${escAttr(href.trim())}">`, '</a>'] : null;
  }
  if (BLOCK_TAGS.has(tag) || INLINE_TAGS.has(tag)) return [`<${tag}>`, `</${tag}>`];
  if (CONTAINER_TAGS.has(tag)) {
    const holdsBlocks = Array.from(node.childNodes).some((c) => BLOCK_LIKE.has(tagOf(c)));
    return holdsBlocks ? null : ['<p>', '</p>'];
  }
  return null;
}

/**
 * The children of `node` as { html, text, block }: `block` says the output
 * holds at least one non-empty block. `ctx.list` is the list tag the items
 * belong to ('ul' | 'ol' | ''), `ctx.first` the 1-based number of the first
 * item at this level (an ol numbers from the item's real position).
 */
function serializeChildren(node, ctx) {
  let html = '';
  let text = '';
  let block = false;
  let prev = null;
  let item = ctx.first;
  node.childNodes.forEach((child) => {
    const isItem = tagOf(child) === 'li';
    const out = serializeNode(child, { list: ctx.list, first: 1, item: isItem ? item : 0 });
    if (isItem) item += 1;
    if (out.html === '' && out.text === '') return;
    if (prev && (prev.block || out.block)) {
      text += prev.item && out.item ? '\n' : '\n\n';
    }
    html += out.html;
    text += out.text;
    block = block || out.block;
    prev = out;
  });
  return { html, text, block };
}

function serializeNode(node, ctx) {
  if (!node) return EMPTY;
  if (node.nodeType === TEXT) {
    const raw = node.nodeValue || '';
    return { html: escText(raw), text: raw, block: false };
  }
  if (node.nodeType === FRAGMENT) return serializeChildren(node, ctx);
  if (node.nodeType !== ELEMENT) return EMPTY;
  const tag = tagOf(node);
  if (tag === 'br') return { html: '<br>', text: '\n', block: false };

  const isList = tag === 'ul' || tag === 'ol';
  const inner = serializeChildren(node, { list: isList ? tag : ctx.list, first: 1 });
  const wrap = wrapperOf(node);
  if (!wrap) return inner;                               // unwrapped: transparent
  if (inner.html === '') return EMPTY;                   // an empty block clones at a boundary offset
  const isBlock = BLOCK_TAGS.has(tag) || wrap[0] === '<p>';
  let text = inner.text;
  if (tag === 'li') text = `${ctx.list === 'ol' ? `${ctx.item || 1}. ` : '• '}${text}`;
  return { html: `${wrap[0]}${inner.html}${wrap[1]}`, text, block: isBlock, item: tag === 'li' };
}

const elementOf = (node) => (node && node.nodeType === ELEMENT ? node : node?.parentNode || null);

/**
 * The list the range starts in, inside `box`: its tag ('' when none) and the
 * 1-based position of the first selected item — the li holding the start, or,
 * when the start sits on the ul/ol itself, the child its offset points at.
 */
function listContextOf(range, box) {
  const el = elementOf(range.startContainer);
  const startTag = tagOf(el);
  let list = null;
  let li = null;
  if (startTag === 'ul' || startTag === 'ol') {
    list = el;
    li = el.childNodes[range.startOffset] || null;
  } else {
    li = el && typeof el.closest === 'function' ? el.closest('li') : null;
    list = li ? li.parentNode : null;
  }
  if (!list || !box.contains(list)) return { list: '', first: 1 };
  const items = Array.from(list.childNodes).filter((c) => tagOf(c) === 'li');
  return { list: tagOf(list), first: items.indexOf(li) + 1 || 1 };
}

/** The clipboard payload of `range` inside `box`, or null when the browser should copy. */
export function serializeSelection(range, box) {
  if (!range || !box || range.collapsed) return null;
  if (!box.contains(range.startContainer)) return null;
  const startEl = elementOf(range.startContainer);
  const source = startEl && typeof startEl.closest === 'function' ? startEl.closest(SOURCE_SELECTOR) : null;
  if (source && box.contains(source)) return null;

  let r = range;
  if (!box.contains(range.endContainer)) {
    const tail = range.cloneRange();
    tail.setStart(box, box.childNodes.length);
    if (tail.toString().trim() !== '') return null;      // the selection really goes on past the box
    r = range.cloneRange();
    r.setEnd(box, box.childNodes.length);
    if (r.collapsed) return null;
  }

  const fragment = r.cloneContents();
  const out = serializeChildren(fragment, listContextOf(r, box));
  if (out.html === '' || out.text.trim() === '') return null;

  let { html } = out;
  let node = elementOf(r.commonAncestorContainer);
  while (node && node !== box && box.contains(node)) {
    const tag = tagOf(node);
    if (INLINE_TAGS.has(tag) || (out.block && BLOCK_TAGS.has(tag))) {
      const wrap = wrapperOf(node);
      if (wrap) html = `${wrap[0]}${html}${wrap[1]}`;
    }
    node = node.parentNode;
  }
  return { html, text: out.text };
}

/** React onCopy handler for a value box: `boxRef.current` is the box element. */
export const onCopyWithinBox = (boxRef) => (event) => {
  const box = boxRef && boxRef.current;
  const data = event && event.clipboardData;
  if (!box || !data || typeof data.setData !== 'function') return;
  const doc = box.ownerDocument || document;
  const selection = typeof doc.getSelection === 'function' ? doc.getSelection() : null;
  if (!selection || selection.rangeCount !== 1) return;
  const out = serializeSelection(selection.getRangeAt(0), box);
  if (!out) return;
  data.setData('text/html', out.html);
  data.setData('text/plain', out.text);
  event.preventDefault();
};
